import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, X, PhoneOff, MailX, Ban } from "lucide-react";
import { V } from "@/lib/vocab";

const Flag = ({ icon: Icon, label, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold">
    <Icon className="h-3 w-3" /> {label}
  </span>
);

export const PersonFlags = ({ p }) => (
  <>
    {p.stopped ? <Flag icon={Ban} label="Texted STOP" testId={`flag-stop-${p.id}`} />
      : !p.has_phone ? <Flag icon={PhoneOff} label="No mobile" testId={`flag-nophone-${p.id}`} />
        : !p.sms_ok ? <Flag icon={PhoneOff} label="Not opted in" testId={`flag-optin-${p.id}`} /> : null}
    {!p.has_email && <Flag icon={MailX} label="No email" testId={`flag-noemail-${p.id}`} />}
  </>
);

// Roster search + chips. `selected` is [{id, name, unit, sms_ok, has_email, ...}].
export const PeopleSearch = ({ tenantId, selected, onChange, max = 50, testId = "people", placeholder = "Search by name, unit, address or phone…" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!q.trim() || !tenantId) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/tenants/${tenantId}/messages/people?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults(r.data || [])).catch(() => setResults([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q, tenantId]);

  const add = (p) => {
    if (selected.some((s) => s.id === p.id) || selected.length >= max) return;
    onChange([...selected, p]);
    setQ(""); setResults([]);
  };
  const remove = (id) => onChange(selected.filter((s) => s.id !== id));
  const fresh = results.filter((r) => !selected.some((s) => s.id === r.id));

  return (
    <div data-testid={`${testId}-picker`}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testId}-chips`}>
          {selected.map((p) => (
            <span key={p.id} data-testid={`${testId}-chip-${p.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white pl-2.5 pr-1 py-1 text-xs font-semibold text-ink max-w-full">
              <span className="truncate">{p.name}{p.unit ? <span className="text-ink/45 font-normal"> · {p.unit}</span> : null}</span>
              <PersonFlags p={p} />
              <button type="button" aria-label={`Remove ${p.name}`} data-testid={`${testId}-remove-${p.id}`} onClick={() => remove(p.id)}
                className="rounded-full p-0.5 text-ink/40 hover:bg-sand-100 hover:text-ink"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/35" />
        <input data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={selected.length >= max ? `Up to ${max} ${V.lcs("person")}` : placeholder}
          disabled={selected.length >= max}
          className="w-full rounded-xl border border-sand-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50" />
        {q.trim() && (
          <div data-testid={`${testId}-results`} className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-sand-200 bg-white shadow-lg">
            {fresh.length === 0 ? (
              <div className="px-3 py-2.5 text-xs text-ink/50">{results.length ? "Already picked" : "No one on the roster matches"}</div>
            ) : fresh.map((p) => (
              <button key={p.id} type="button" data-testid={`${testId}-result-${p.id}`} onClick={() => add(p)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-sand-50">
                <span className="min-w-0 truncate">
                  <span className="font-semibold text-ink">{p.name}</span>
                  {(p.unit || p.address) && <span className="text-ink/50"> · {p.unit || p.address}</span>}
                </span>
                <span className="flex items-center gap-1 shrink-0"><PersonFlags p={p} /></span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
